'use client';

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { isUsageDepleted, normalizeHeroTier, normalizeHeroUsageSnapshot, usageMetadataPropType } from '@/utils/normalization';
import PropTypes from 'prop-types';

const HeroUsageContext = createContext(null);

/**
 * Provides tier-aware quota state to the hero experience. Initializes state
 * from the SSR snapshot, keeps upgrade modal visibility in sync, and exposes
 * helpers that child components can use to update usage after API calls.
 *
 * @param {{ limit: number|null, remaining: number|null, resetAt: string|null } | null} initialUsage
 * @param {'anonymous'|'free'|'premium'} initialTier
 * @param {React.ReactNode} children
 */
export function HeroUsageProvider({ initialUsage = null, initialTier = 'anonymous', children }) {
  const normalizedInitialTier = normalizeHeroTier(initialTier);
  const normalizedInitialUsage = normalizeHeroUsageSnapshot(initialUsage);
  const prefetchedUsageAvailableRef = useRef(Boolean(normalizedInitialUsage));
  const [usageMetadata, setUsageMetadata] = useState(normalizedInitialUsage);
  const [userTier, setUserTier] = useState(normalizedInitialTier);
  const [dailyUsesRemaining, setDailyUsesRemaining] = useState(
    typeof normalizedInitialUsage?.remaining === 'number' ? normalizedInitialUsage.remaining : null
  );
  const [dailyLimitReached, setDailyLimitReached] = useState(isUsageDepleted(normalizedInitialUsage));
  const [forceUpgradeModal, setForceUpgradeModal] = useState(
    Boolean(dailyLimitReached && normalizedInitialTier !== 'premium')
  );
  const [prefetchTimestamp] = useState(() => (prefetchedUsageAvailableRef.current ? Date.now() : null));

  const applyUsageSnapshot = useCallback((snapshot, nextTier) => {
    const normalizedSnapshot = normalizeHeroUsageSnapshot(snapshot);
    const resolvedTier = normalizeHeroTier(nextTier ?? userTier);

    if (resolvedTier === 'premium' && userTier === 'anonymous') {
      console.warn('[HeroUsageProvider] Rejected premium tier upgrade for anonymous user');
      return;
    }

    setUsageMetadata(normalizedSnapshot);
    setUserTier(resolvedTier);

    if (normalizedSnapshot && typeof normalizedSnapshot.remaining === 'number') {
      const remaining = normalizedSnapshot.remaining;
      setDailyUsesRemaining(remaining);
      const depleted = isUsageDepleted(normalizedSnapshot);
      setDailyLimitReached(depleted);
      setForceUpgradeModal(depleted && resolvedTier !== 'premium');
    } else {
      setDailyUsesRemaining(null);
      setDailyLimitReached(false);
      setForceUpgradeModal(false);
    }
  }, [userTier]);

  const clearUsageState = useCallback(() => {
    setUsageMetadata(null);
    setDailyUsesRemaining(null);
    setDailyLimitReached(false);
    setForceUpgradeModal(false);
  }, []);

  const markLimitReached = useCallback(() => {
    setDailyUsesRemaining(0);
    setDailyLimitReached(true);
    setForceUpgradeModal((current) => current || userTier !== 'premium');
  }, [userTier]);

  const dismissUpgradeModal = useCallback(() => {
    setForceUpgradeModal(false);
  }, []);

  const requestUpgradeModal = useCallback(() => {
    setForceUpgradeModal(true);
  }, []);

  const contextValue = useMemo(() => ({
    usageMetadata,
    userTier,
    dailyUsesRemaining,
    dailyLimitReached,
    forceUpgradeModal,
    hasInitialUsage: prefetchedUsageAvailableRef.current,
    isPremiumTier: userTier === 'premium',
    prefetchTimestamp,
    applyUsageSnapshot,
    clearUsageState,
    markLimitReached,
    dismissUpgradeModal,
    requestUpgradeModal
  }), [
    usageMetadata,
    userTier,
    dailyUsesRemaining,
    dailyLimitReached,
    forceUpgradeModal,
    prefetchTimestamp,
    applyUsageSnapshot,
    clearUsageState,
    markLimitReached,
    dismissUpgradeModal,
    requestUpgradeModal
  ]);

  return (
    <HeroUsageContext.Provider value={contextValue}>
      {children}
    </HeroUsageContext.Provider>
  );
}

/**
 * Access the current hero usage state. Throws if the provider is missing so
 * tests and stories surface configuration issues immediately.
 *
 * @returns {{ usageMetadata, userTier, dailyUsesRemaining, dailyLimitReached, forceUpgradeModal, hasInitialUsage, isPremiumTier, prefetchTimestamp, applyUsageSnapshot: Function, clearUsageState: Function, markLimitReached: Function, dismissUpgradeModal: Function }}
 */
export function useHeroUsage() {
  const context = useContext(HeroUsageContext);

  if (!context) {
    throw new Error('useHeroUsage must be used within a HeroUsageProvider');
  }

  return context;
}

HeroUsageProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialUsage: PropTypes.oneOfType([usageMetadataPropType, PropTypes.oneOf([null])]),
  initialTier: PropTypes.oneOf(['anonymous', 'free', 'premium']),
};

